import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFeature } from '../../hooks/useFeature';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import promptState, { SHEET_LIMIT, HIDE_DAYS } from './promptState';
import NotificationPromptSheet from './NotificationPromptSheet';
import NotificationOffBar from './NotificationOffBar';
import './notifications.css';
import { STAFF_ROLES } from '../../utils/access';

const DAY_MS = 24 * 60 * 60 * 1000;
const TOAST_MS = 4000;

/**
 * Invites people to turn on notifications: a sheet, then a small bar.
 * place: 'home' (the dashboard) or 'afterBooking' (a booking confirmation)
 */
export default function NotificationPrompt({ place }) {
  const featureOn = useFeature('notifications');
  const { user } = useAuth();
  // The hook looks at the browser and may call the server, so it only runs
  // for people who could be prompted at all
  const role = user?.role;
  if (!featureOn || !user || role === 'SUPER_ADMIN' || role === 'CHILD' || user.actingAs) return null;
  return <PromptInner place={place} user={user} />;
}

function PromptInner({ place, user }) {
  const { platform, isSubscribed, checked, loading, error, subscribe } = usePushNotifications();

  // The sheet's kind while it's open, or null. Kept in state so the sheet
  // stays put if the platform changes under it (a refusal makes it 'blocked')
  const [sheetKind, setSheetKind] = useState(null);
  const [barOn, setBarOn] = useState(false);
  const [turningOn, setTurningOn] = useState(false);
  // The message at the bottom: 'working' (allowed, still setting up), 'on',
  // or 'failed' (set-up failed after the sheet closed), else null
  const [toast, setToast] = useState(null);
  const [failure, setFailure] = useState(null);
  const decided = useRef(false);

  const kind = platform.kind;
  const role = user.role;
  const storeId = user.id;
  const fromBar = useRef(false);
  const barButton = useRef(null);
  // Wait for the first look at existing subscriptions, so a device that is
  // already subscribed never sees (or uses up) the prompt
  const eligible = checked && !isSubscribed && kind !== 'blocked' && kind !== 'unsupported';

  // Decide once per visit what to show
  useEffect(() => {
    if (!eligible || decided.current) return;
    decided.current = true;
    const saved = promptState.read(storeId);
    if (kind === 'ios-old') {
      if (saved.iosOld) return;
      promptState.write(storeId, { iosOld: true });
      setSheetKind('ios-old');
    } else if (place === 'afterBooking') {
      if (saved.afterFirstBookingShown) return;
      promptState.write(storeId, { afterFirstBookingShown: true });
      setSheetKind(kind);
    } else if (saved.hiddenUntil && saved.hiddenUntil > Date.now()) {
      // Hidden for now
    } else if (saved.sheetDismissals < SHEET_LIMIT) {
      setSheetKind(kind);
    } else {
      setBarOn(true);
    }
  }, [eligible, kind, place, storeId]);

  // Turned on from the sheet: put everything away and say so briefly
  useEffect(() => {
    if (turningOn && isSubscribed) {
      setTurningOn(false);
      setSheetKind(null);
      setBarOn(false);
      setToast('on');
    }
  }, [turningOn, isSubscribed]);

  useEffect(() => {
    if (toast !== 'on' && toast !== 'failed') return undefined;
    const timer = setTimeout(() => setToast(null), toast === 'failed' ? TOAST_MS * 2 : TOAST_MS);
    return () => clearTimeout(timer);
  }, [toast]);

  // The sheet opened from the bar: when it closes, focus goes back to the bar's button
  const open = !!sheetKind && !isSubscribed;
  useEffect(() => {
    if (!open && fromBar.current) {
      fromBar.current = false;
      if (barButton.current) barButton.current.focus();
    }
  }, [open]);

  const closeSheet = () => {
    const wasOld = sheetKind === 'ios-old';
    setSheetKind(null);
    setTurningOn(false);
    if (wasOld || place !== 'home') return;
    promptState.write(storeId, { sheetDismissals: promptState.read(storeId).sheetDismissals + 1 });
    setBarOn(true);
  };
  // "Got it": understood, so no dismissal is counted, but the bar stays on offer
  const gotIt = () => {
    const wasOld = sheetKind === 'ios-old';
    setSheetKind(null);
    setTurningOn(false);
    if (!wasOld && place === 'home') setBarOn(true);
  };
  const hideBar = () => {
    promptState.write(storeId, { hiddenUntil: Date.now() + HIDE_DAYS * DAY_MS });
    setBarOn(false);
  };
  // To the Notifications page; like "Got it", not a "not now"
  const navigate = useNavigate();
  const choose = () => {
    gotIt();
    navigate('/booking/notifications');
  };

  // The sheet closes as soon as the browser's Allow is given; setting up can
  // take a few seconds more, so a message says what's happening
  const turnOn = async () => {
    setTurningOn(true);
    let closedEarly = false;
    await subscribe({
      onGranted: () => {
        closedEarly = true;
        setSheetKind(null);
        setToast('working');
      },
    });
    if (closedEarly) setFailure('pending');
  };

  // Closed early but set-up then failed: say why, and offer the bar again
  useEffect(() => {
    if (failure !== 'pending') return;
    setFailure(null);
    if (isSubscribed) return;
    setTurningOn(false);
    if (error) {
      setToast('failed');
      if (place === 'home') setBarOn(true);
    } else {
      setToast(null);
    }
  }, [failure, isSubscribed, error, place]);

  const sheetOpen = !!sheetKind && !isSubscribed;
  const barShown = barOn && !sheetOpen && eligible && kind !== 'ios-old';

  return (
    <>
      {barShown && <NotificationOffBar turnOnRef={barButton} onTurnOn={() => { fromBar.current = true; setSheetKind(kind); }} onHide={hideBar} />}
      {sheetOpen && (
        <NotificationPromptSheet
          kind={sheetKind}
          staff={STAFF_ROLES.includes(role)}
          browser={platform.browser}
          loading={loading}
          error={error}
          onTurnOn={turnOn}
          onClose={closeSheet}
          onGotIt={gotIt}
          onChoose={choose}
        />
      )}
      {toast === 'working' && <div className="notif-toast" role="status">Turning notifications on…</div>}
      {toast === 'on' && <div className="notif-toast" role="status">Notifications are on</div>}
      {toast === 'failed' && <div className="notif-toast" role="alert">{error}</div>}
    </>
  );
}
